import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DataService } from '../../services/data.service';
import { MaterialModule } from '../../../../../../../modules/catch-core/third-party/material/material.module';
import { FormsModule } from '@angular/forms';

interface TargetField {
  id?: string;
  name: string;
  fieldType?: string;
}

interface ShapeData {
  name: string;
}

interface DeleteDialogData {
  data: {
    catchindex?: number;
    bindingName: string;
    targetField: TargetField;
    shape: ShapeData;
    defaultColor: string;
    colorData?: Array<{ value: string; color: string }>;
  };
}

@Component({
  selector: 'app-delete-dialog',
  templateUrl: './delete-dialog.component.html',
  styleUrls: ['./delete-dialog.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
})
export class DeleteDialogComponent implements OnInit {
  // Inject dependencies using Angular 20 inject() function
  public dialogRef = inject(MatDialogRef<DeleteDialogComponent>);
  public deleteData = inject<DeleteDialogData>(MAT_DIALOG_DATA);
  public dataService = inject(DataService);

  // Component properties
  public columnConfigurations: any[] = [];
  public bindingName = '';
  public targetField: TargetField = { name: '' };
  public selectedShape = '';
  public defaultColor = '';
  public colorData: Array<{ value: string; color: string }> = [];
  public catchIndex?: number;

  ngOnInit(): void {
    this.initializeComponent();
  }

  /**
   * Initialize component with data from dialog
   */
  private initializeComponent(): void {
    try {
      this.columnConfigurations = this.dataService.getTableConfiguration();

      if (this.deleteData?.data) {
        const data = this.deleteData.data;
        this.bindingName = data.bindingName || '';
        this.targetField = data.targetField || { name: '' };
        this.selectedShape = data.shape?.name || '';
        this.defaultColor = data.defaultColor || '';
        this.colorData = data.colorData || [];
        this.catchIndex = data.catchindex;
      }
    } catch (error) {
      console.error('Error initializing delete dialog:', error);
    }
  }

  /**
   * Close dialog without deleting
   */
  public onNoClick(): void {
    this.dialogRef.close(0);
  }

  /**
   * Confirm deletion and close dialog with success code
   */
  public confirmDelete(): void {
    if (this.catchIndex !== undefined) {
      this.dataService.deleteRecord(this.catchIndex);
      this.dialogRef.close(1);
    } else {
      console.error('Cannot delete: catchIndex is undefined');
      this.dialogRef.close(0);
    }
  }

  /**
   * Get formatted display of target field
   */
  public getTargetFieldDisplay(): string {
    return this.targetField?.name || 'N/A';
  }

  /**
   * Get formatted display of shape
   */
  public getShapeDisplay(): string {
    return this.selectedShape || 'N/A';
  }

  /**
   * Check if color data exists for display
   */
  public hasColorData(): boolean {
    return this.colorData && this.colorData.length > 0;
  }

  /**
   * Get color data items for display
   */
  public getColorDataItems(): Array<{ value: string; color: string }> {
    return this.colorData || [];
  }
}
